import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { TERMS, CATEGORIES, categoryLabel } from "@/data/glossary";
import { PublicShell, SignupCta, useSeo } from "./PublicShell";

const INTROS = {
  "autism-neurodiversity": "Core terms that describe autism, neurodivergence, and how autistic children experience and navigate the world.",
  "diagnosis-assessment": "Words families encounter during the assessment and diagnosis journey in Canada.",
  "therapy-support": "Common therapies and support approaches, explained in plain, respectful language.",
  "school-education": "Terms you'll see in school plans, meetings, and special education across Canadian school boards.",
  "funding-benefits": "Federal tax credits and benefits that may help Canadian families with the costs of care.",
  "ontario-programs": "Ontario-specific programs and the terms families see when applying for support.",
  "provincial-programs": "Autism and disability programs across Canada's provinces and territories, with terms families see when applying.",
  "co-occurring": "Conditions and differences that often occur alongside autism.",
};

export default function CategoryHub() {
  const { key } = useParams();
  const cat = CATEGORIES.find((c) => c.key === key);
  const items = TERMS.filter((t) => t.category === key).sort((a, b) => a.title.localeCompare(b.title));

  useSeo({
    title: cat ? `${cat.label} — Autism Glossary for Canadian Families | KidPath` : "Category not found | KidPath",
    description: cat ? `Plain-language definitions of ${cat.label.toLowerCase()} terms for Canadian autism families. ${INTROS[key] || ""}` : undefined,
    noindex: !cat,
    jsonLd: cat ? {
      "@context": "https://schema.org", "@type": "DefinedTermSet",
      name: `KidPath Glossary — ${cat.label}`, url: `https://kidpath.ca/terms/category/${key}/`,
      hasDefinedTerm: items.map((t) => ({ "@type": "DefinedTerm", name: t.title, url: `https://kidpath.ca/terms/${t.slug}/` })),
    } : undefined,
  });

  if (!cat) {
    return (
      <PublicShell>
        <div className="max-w-[760px] mx-auto px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-extrabold mb-3">Category not found</h1>
          <Link to="/terms" className="inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white font-semibold px-5 py-2.5" data-testid="cat-back-to-glossary">Browse the glossary</Link>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <section className="bg-gradient-to-b from-kid-skytint/50 to-white border-b border-kid-line">
        <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-12">
          <nav className="flex items-center gap-1.5 text-sm text-kid-muted mb-5" data-testid="cat-breadcrumbs">
            <Link to="/terms" className="hover:text-kid-teal">Glossary</Link>
            <ChevronRight size={15} />
            <span className="text-kid-navy font-medium">{cat.label}</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-kid-tealcta mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-kid-coral" /> Glossary category
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold" data-testid="cat-title">{cat.label}</h1>
          <p className="text-kid-muted text-lg mt-3 max-w-2xl">{INTROS[key]}</p>
        </div>
      </section>

      <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10">
        <p className="text-sm text-kid-muted mb-6" data-testid="cat-result-count">{items.length} {items.length === 1 ? "term" : "terms"}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <Link key={t.slug} to={`/terms/${t.slug}`} data-testid={`cat-term-${t.slug}`} className="kid-card kid-card-interactive p-5 block">
              <div className="font-bold text-kid-navy text-lg leading-snug">{t.title}{t.acronym ? <span className="text-kid-muted font-semibold"> ({t.acronym})</span> : null}</div>
              <p className="text-sm text-kid-muted mt-1.5 line-clamp-2">{t.definition}</p>
            </Link>
          ))}
        </div>

        <div className="mt-12"><SignupCta variant="band" testid="cat-cta" /></div>

        <div className="mt-10">
          <h2 className="font-display text-xl font-extrabold mb-4">Browse other categories</h2>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.filter((c) => c.key !== key).map((c) => (
              <Link key={c.key} to={`/terms/category/${c.key}`} className="text-sm font-semibold rounded-full px-4 py-2 border border-kid-line text-kid-muted hover:text-kid-navy hover:border-kid-teal transition-colors" data-testid={`cat-other-${c.key}`}>{c.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </PublicShell>
  );
}
